if (!customElements.get('nl-product-3d')) {
  let viewerReady;
  const loadViewer = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!viewerReady) {
      viewerReady = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        const timeout = setTimeout(() => {
          script.remove();
          reject(new Error('viewer_error'));
        }, 20000);
        script.type = 'module';
        script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/4.4.0/model-viewer.min.js';
        script.onload = () => customElements.whenDefined('model-viewer').then(() => {
          clearTimeout(timeout);
          resolve();
        });
        script.onerror = () => {
          clearTimeout(timeout);
          script.remove();
          reject(new Error('viewer_error'));
        };
        document.head.append(script);
      }).catch(error => {
        viewerReady = null;
        throw error;
      });
    }
    return viewerReady;
  };

  customElements.define('nl-product-3d', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      try {
        this.models = JSON.parse(this.querySelector('[data-models]').textContent);
        this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      } catch {
        return;
      }
      this.ready = true;
      this.events = new AbortController();
      const options = {signal: this.events.signal};
      this.launch = this.querySelector('[data-open-3d]');
      this.dialog = this.querySelector('dialog');
      this.stage = this.querySelector('[data-3d-stage]');
      this.canvas = this.querySelector('[data-3d-canvas]');
      this.poster = this.querySelector('[data-3d-poster]');
      this.status = this.querySelector('[data-3d-status]');
      this.progress = this.querySelector('[data-3d-progress]');
      this.fallback = this.querySelector('[data-3d-fallback]');
      this.reset = this.querySelector('[data-reset-3d]');
      this.expand = this.querySelector('[data-expand-3d]');
      this.variantId = this.dataset.variant;
      this.launch.addEventListener('click', () => this.open(), options);
      for (const button of this.querySelectorAll('[data-close-3d],[data-back-3d]')) {
        button.addEventListener('click', () => this.dialog.close(), options);
      }
      this.querySelector('[data-retry-3d]').addEventListener('click', () => this.renderModel(), options);
      this.querySelector('[data-3d-sketch]')?.addEventListener('click', () => this.dialog.close(), options);
      this.reset.addEventListener('click', () => {
        const viewer = this.stage.querySelector('model-viewer');
        if (!viewer) return;
        viewer.cameraOrbit = '0deg 75deg 105%';
        viewer.cameraTarget = 'auto auto auto';
        viewer.fieldOfView = 'auto';
        viewer.jumpCameraToGoal();
      }, options);
      this.expand.addEventListener('click', () => {
        const wide = this.dialog.classList.toggle('is-wide');
        this.expand.setAttribute('aria-pressed', String(wide));
        this.expand.textContent = wide ? this.labels.collapse : this.labels.expand;
      }, options);
      this.dialog.addEventListener('close', () => {
        this.cancelModel();
        this.launch.focus({preventScroll: true});
      }, options);
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }, options);
      this.variants = this.closest('product-page')?.querySelector('product-variants[data-main-product-variants]');
      this.variants?.addEventListener('VARIANT_CHANGE', event => this.selectVariant(event.target.currentVariant?.id), options);
      this.selectVariant(this.variants?.currentVariant?.id || this.variantId);
    }

    disconnectedCallback() {
      this.cancelModel();
      this.events?.abort();
      this.ready = false;
    }

    selectVariant(id) {
      this.variantId = String(id || '');
      this.hidden = !this.models[this.variantId];
      if (this.dialog.open) this.dialog.close();
    }

    open() {
      if (!this.models[this.variantId]) return;
      this.dialog.showModal();
      this.track('open');
      this.renderModel();
    }

    cancelModel() {
      this.requestId = (this.requestId || 0) + 1;
      clearTimeout(this.modelTimer);
      this.stage?.replaceChildren();
      this.canvas?.setAttribute('aria-busy', 'false');
    }

    async renderModel() {
      const info = this.models[this.variantId];
      if (!info || !this.dialog.open) return;
      this.cancelModel();
      const requestId = this.requestId;
      this.openedAt = performance.now();
      this.dialog.dataset.state = 'loading';
      this.querySelector('.nl6-3d-badge').textContent = '3D';
      this.canvas.setAttribute('aria-busy', 'true');
      this.status.textContent = this.labels.loading;
      this.progress.removeAttribute('value');
      this.progress.hidden = false;
      this.fallback.hidden = true;
      this.reset.disabled = true;
      this.poster.hidden = !info.image;
      if (info.image) this.poster.src = info.image;
      this.poster.alt = [this.dataset.title, info.variant === 'Default Title' ? '' : info.variant].filter(Boolean).join(' · ');
      this.querySelector('[data-3d-variant]').textContent = [info.variant === 'Default Title' ? this.labels.default_variant : info.variant, info.sku].filter(Boolean).join(' · ');
      try {
        const probe = document.createElement('canvas');
        const graphics = probe.getContext('webgl2') || probe.getContext('webgl');
        if (!graphics) throw new Error('unsupported');
        graphics.getExtension('WEBGL_lose_context')?.loseContext();
        if (navigator.onLine === false) throw new Error('connection_error');
        await loadViewer();
        if (!this.dialog.open || requestId !== this.requestId) return;
        const viewer = document.createElement('model-viewer');
        viewer.setAttribute('src', info.source.glb);
        if (info.source.usdz) viewer.setAttribute('ios-src', info.source.usdz);
        if (info.image) viewer.setAttribute('poster', info.image);
        viewer.setAttribute('alt', this.poster.alt);
        viewer.setAttribute('camera-controls', '');
        viewer.setAttribute('camera-orbit', '0deg 75deg 105%');
        viewer.setAttribute('touch-action', 'pan-y');
        viewer.setAttribute('shadow-intensity', '0.6');
        viewer.setAttribute('shadow-softness', '1');
        viewer.setAttribute('ar', '');
        viewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
        viewer.setAttribute('ar-scale', 'fixed');
        if (this.dataset.placement === 'wall') viewer.setAttribute('ar-placement', 'wall');
        viewer.setAttribute('loading', 'eager');
        const ar = document.createElement('button');
        ar.type = 'button';
        ar.slot = 'ar-button';
        ar.className = 'nl6-3d-ar';
        ar.textContent = this.labels.ar;
        viewer.append(ar);
        this.modelTimer = setTimeout(() => {
          if (requestId === this.requestId) this.fail(navigator.onLine === false ? 'connection_error' : 'error');
        }, 30000);
        viewer.addEventListener('progress', event => {
          if (requestId !== this.requestId || this.dialog.dataset.state !== 'loading') return;
          const value = event.detail?.totalProgress;
          if (Number.isFinite(value)) this.progress.value = Math.min(1, Math.max(0, value));
        });
        viewer.addEventListener('load', () => {
          if (requestId !== this.requestId || !this.dialog.open) return;
          clearTimeout(this.modelTimer);
          this.dialog.dataset.state = 'ready';
          this.canvas.setAttribute('aria-busy', 'false');
          this.status.textContent = this.labels.ready;
          this.progress.hidden = true;
          this.reset.disabled = false;
          this.track('loaded', {load_time_ms: Math.round(performance.now() - this.openedAt)});
        }, {once: true});
        viewer.addEventListener('error', () => {
          if (requestId === this.requestId) this.fail(navigator.onLine === false ? 'connection_error' : 'error');
        }, {once: true});
        viewer.addEventListener('ar-status', event => {
          if (requestId !== this.requestId) return;
          if (event.detail?.status === 'session-started') this.track('ar_started');
          if (event.detail?.status === 'failed') {
            this.status.textContent = this.labels.ar_failed;
            this.track('ar_error');
          }
        });
        this.stage.append(viewer);
      } catch (error) {
        if (requestId === this.requestId) this.fail(['unsupported', 'connection_error', 'viewer_error'].includes(error.message) ? error.message : 'error');
      }
    }

    fail(reason) {
      if (!this.dialog.open) return;
      this.cancelModel();
      this.dialog.dataset.state = 'error';
      this.querySelector('.nl6-3d-badge').textContent = this.labels.image;
      this.status.textContent = this.labels[reason] || this.labels.error;
      this.progress.hidden = true;
      this.fallback.hidden = false;
      this.querySelector('[data-retry-3d]').hidden = reason === 'unsupported';
      this.reset.disabled = true;
      this.track('error', {error_type: reason});
    }

    track(action, extra = {}) {
      try {
        if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return;
        if (typeof window.gtag !== 'function') return;
        window.gtag('event', 'product_3d_' + action, {
          product_id: this.dataset.productId,
          variant_id: this.variantId,
          item_id: this.models[this.variantId]?.sku,
          ...extra
        });
      } catch {}
    }
  });
}
